import * as ToastPrimitive from '@radix-ui/react-toast';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  IconAlertOctagon,
  IconAlertTriangle,
  IconCheckCircle,
  IconClose,
  IconInfo,
} from './icons';
import { cn, focusRing } from './utils';

export type ToastTone = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
  readonly title: string;
  readonly description?: string;
  readonly tone?: ToastTone;
  /** Milissegundos. `0` = permanece até o usuário fechar. */
  readonly duration?: number;
  readonly action?: { readonly label: string; readonly onClick: () => void };
}

interface ToastRecord extends ToastOptions {
  readonly id: string;
}

interface ToastContextValue {
  readonly toast: (options: ToastOptions) => void;
  readonly dismissAll: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast precisa estar dentro de <ToastProvider>.');
  return ctx;
}

const TONE_STYLES: Record<ToastTone, { container: string; icon: ReactNode }> = {
  success: {
    container: 'border-success bg-success-subtle text-success-content',
    icon: <IconCheckCircle size={18} />,
  },
  error: {
    container: 'border-danger bg-danger-subtle text-danger-content',
    icon: <IconAlertOctagon size={18} />,
  },
  warning: {
    container: 'border-warning bg-warning-subtle text-warning-content',
    icon: <IconAlertTriangle size={18} />,
  },
  info: {
    container: 'border-info bg-info-subtle text-info-content',
    icon: <IconInfo size={18} />,
  },
};

/**
 * Provedor de notificações efêmeras.
 *
 * ACESSIBILIDADE:
 *  · erros usam `role="alert"` (assertivo) e NÃO expiram sozinhos — uma
 *    mensagem de falha que some em 5 segundos é inacessível para quem lê
 *    devagar, e em ambiente regulado perder a razão da falha é perder evidência;
 *  · sucessos usam `role="status"` (polite) e expiram;
 *  · a região de toasts tem `hotkey` F8 (padrão do Radix) para foco direto;
 *  · cada tom tem ÍCONE próprio além da cor.
 */
export function ToastProvider({ children }: { readonly children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);

  const toast = useCallback((options: ToastOptions) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts((current) => [...current, { ...options, id }]);
  }, []);

  const dismissAll = useCallback(() => setToasts([]), []);

  const value = useMemo<ToastContextValue>(() => ({ toast, dismissAll }), [toast, dismissAll]);

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider swipeDirection="right" duration={6000}>
        {children}

        {toasts.map((item) => {
          const tone = item.tone ?? 'info';
          const isError = tone === 'error';
          const style = TONE_STYLES[tone];

          return (
            <ToastPrimitive.Root
              key={item.id}
              type={isError ? 'foreground' : 'background'}
              duration={item.duration ?? (isError ? Number.POSITIVE_INFINITY : 6000)}
              onOpenChange={(open) => {
                if (!open) setToasts((current) => current.filter((t) => t.id !== item.id));
              }}
              className={cn(
                'pointer-events-auto flex w-full items-start gap-3 rounded-lg border px-4 py-3 shadow-lg',
                'animate-toast-in data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
                'data-[swipe=cancel]:translate-x-0 data-[state=closed]:opacity-0',
                style.container,
              )}
            >
              <span aria-hidden="true" className="mt-0.5 shrink-0">
                {style.icon}
              </span>
              <div className="min-w-0 flex-1">
                <ToastPrimitive.Title className="text-sm font-semibold">
                  {item.title}
                </ToastPrimitive.Title>
                {item.description ? (
                  <ToastPrimitive.Description className="mt-0.5 text-sm">
                    {item.description}
                  </ToastPrimitive.Description>
                ) : null}
                {item.action ? (
                  <ToastPrimitive.Action
                    asChild
                    altText={item.action.label}
                    onClick={item.action.onClick}
                  >
                    <button
                      type="button"
                      className={cn(
                        'mt-2 rounded text-sm font-semibold underline underline-offset-2',
                        focusRing,
                      )}
                    >
                      {item.action.label}
                    </button>
                  </ToastPrimitive.Action>
                ) : null}
              </div>
              <ToastPrimitive.Close
                aria-label="Fechar notificação"
                className={cn(
                  'shrink-0 rounded p-1 opacity-70 hover:opacity-100',
                  focusRing,
                )}
              >
                <IconClose size={15} />
              </ToastPrimitive.Close>
            </ToastPrimitive.Root>
          );
        })}

        <ToastPrimitive.Viewport
          label="Notificações do sistema (pressione F8 para focar)"
          className="pointer-events-none fixed bottom-0 right-0 z-toast flex w-[min(26rem,100vw)] flex-col gap-2 p-4"
        />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}
